import { DISMISS_MODAL, LOG_OUT_BIDDER } from './actions';
import { fetchBidderDataIfNeeded, getAuthToken, getBidderId } from './user';
import { getIsStripeCustomer, getStripeCreditCard } from './stripe';
import {
    getLAPaymentCards,
    postAddLACard,
    postAddStripeCard,
    postUpdateLACard,
    putAddStripeCard,
} from '@/redux/api/payment';
import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import ms from 'ms';
import paymentProviders from '@/enums/paymentProviders';

const REDUX_STORE_TIME = ms('20m');

/* Action Types */
export const ADD_LA_PAYMENT_CARD_FAIL = 'la/ui/addLAPaymentCard/FAIL';
export const ADD_LA_PAYMENT_CARD_REQUEST = 'la/ui/addLAPaymentCard/REQUEST';
export const ADD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/addLAPaymentCard/SUCCESS';

const UPDATE_LA_PAYMENT_CARD_FAIL = 'la/ui/updateLAPaymentCard/FAIL';
const UPDATE_LA_PAYMENT_CARD_REQUEST = 'la/ui/updateLAPaymentCard/REQUEST';
const UPDATE_LA_PAYMENT_CARD_SUCCESS = 'la/ui/updateLAPaymentCard/SUCCESS';

const LOAD_LA_PAYMENT_CARD_FAIL = 'la/ui/loadLAPaymentCard/FAIL';
const LOAD_LA_PAYMENT_CARD_REQUEST = 'la/ui/loadLAPaymentCard/REQUEST';
const LOAD_LA_PAYMENT_CARD_SUCCESS = 'la/ui/loadLAPaymentCard/SUCCESS';

// reducer
export const DEFAULT_STATE = {
    addSubmitted: false,
    addSuccess: false,
    allowedToAddCards: true,
    error: undefined,
    laPaymentCards: [],
    loaded: undefined,
    loading: false,
    updateSubmitted: false,
    updateSuccess: false,
};

export type State = typeof DEFAULT_STATE & {
    error: string | undefined;
    loaded: Date | undefined;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case ADD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                addSubmitted: true,
                addSuccess: false,
                error: undefined,
            };
        case ADD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: true,
                laPaymentCards: [...state.laPaymentCards, action.payload],
                loaded: undefined,
            };
        case LOAD_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                loaded: undefined,
                loading: true,
            };
        case LOAD_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                allowedToAddCards: action.payload.allowedToAddCards,
                error: undefined,
                laPaymentCards: action.payload.cards,
                loaded: new Date(),
                loading: false,
            };
        case LOAD_LA_PAYMENT_CARD_FAIL:
        case ADD_LA_PAYMENT_CARD_FAIL:
        case UPDATE_LA_PAYMENT_CARD_FAIL:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: false,
                error: action.payload,
                loading: false,
                updateSubmitted: false,
            };
        case UPDATE_LA_PAYMENT_CARD_REQUEST:
            return {
                ...state,
                error: undefined,
                updateSubmitted: true,
                updateSuccess: false,
            };
        case UPDATE_LA_PAYMENT_CARD_SUCCESS:
            return {
                ...state,
                error: undefined,
                loaded: undefined,
                updateSubmitted: false,
                updateSuccess: false,
            };
        case DISMISS_MODAL:
            return {
                ...state,
                addSubmitted: false,
                addSuccess: false,
                error: undefined,
            };
        case LOG_OUT_BIDDER:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.creditCard;
const passthroughSelector = (state: GlobalState, id) => id;
//This is only used by stripe on checkout. When stripe is removed this can be too.
export const getUiAddCreditCard = createSelector(stateSelector, (state) => ({
    error: state.error,
    submitted: state.addSubmitted,
    success: state.addSuccess,
}));
export const getAllowedToAddCards = createSelector([stateSelector], (state) => state.allowedToAddCards);
export const getLAPaymentCardsLoaded = createSelector([stateSelector], (state) => state.loaded);
export const getLAPaymentCardsLoading = createSelector([stateSelector], (state) => state.loading);
export const getLAPaymentCardsAddSubmitted = createSelector([stateSelector], (state) => state.addSubmitted);
export const getLAPaymentCardsAddSuccess = createSelector([stateSelector], (state) => state.addSuccess);
export const getLAPaymentCardsUpdateSubmitted = createSelector([stateSelector], (state) => state.updateSubmitted);
export const getLAPaymentCardsUpdateSuccess = createSelector([stateSelector], (state) => state.updateSuccess);
export const getCreditCardError = createSelector([stateSelector], (state) => state.error);

export const creditCardsAnyLoadingSelector = createSelector(
    [getLAPaymentCardsLoading, getLAPaymentCardsAddSubmitted, getLAPaymentCardsUpdateSubmitted],
    (loading, addSubmitted, updateSubmitted) => loading || addSubmitted || updateSubmitted
);

/**
 * returns all cards on file
 * @function laPaymentCardsSelector
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
export const laPaymentCardsSelector = createSelector([stateSelector], (state) => state.laPaymentCards);

export const laPaymentCardsForProviderSelector = createSelector(
    [laPaymentCardsSelector, passthroughSelector],
    (allLaPaymentCards, providerId) => allLaPaymentCards?.filter((cof) => cof.providerId === providerId)
);

/**
 * returns first matching card on file that is a defaultCard and matches providerId
 * @function laPaymentDefaultCardSelector
 * @category Credit Card Service
 * @param {GlobalState}, number
 */
const laPaymentDefaultCardSelector = createSelector(
    [laPaymentCardsSelector, passthroughSelector],
    (laPaymentCards, providerId) => laPaymentCards?.find((card) => card.defaultCard && card.providerId === providerId)
);

//This intentionally only checks la payment cards instead of the userData flag. We don't care if they have a stripe card on file for this check.
//TODO: Should eventually check based on the authed card status.
export const getHasValidCardOnFile = createSelector([laPaymentDefaultCardSelector], (laPaymentCard) =>
    Boolean(laPaymentCard?.token)
);

export const getDefaultCreditCard = (state: any, paymentProvider: number) => {
    if (paymentProvider === paymentProviders.STRIPE) {
        return getStripeCreditCard(state);
    } else {
        return laPaymentDefaultCardSelector(state, paymentProvider);
    }
};

export const getShouldCollectBillingAddress = (state: any, paymentProvider: number) => {
    if (paymentProvider === paymentProviders.STRIPE) {
        return false;
    } else {
        const creditCard = laPaymentDefaultCardSelector(state, paymentProvider);
        return Boolean(creditCard) && !Boolean(creditCard.address.address1);
    }
};

/* ACTION CREATORS */
export type SubmitAddCreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: any;
    houseId: number;
    providerId: number;
};
export const submitAddCreditCard =
    ({
        address,
        cardholderName,
        cardToken,
        houseId,
        providerId = paymentProviders.STRIPE,
    }: SubmitAddCreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);

        // Ensure bidder profile is loaded
        if (bidderId > 0) {
            await dispatch(fetchBidderDataIfNeeded(authToken, true));
        }

        if (providerId === paymentProviders.STRIPE) {
            return await dispatch(addStripeCreditCard({ cardholderName, cardToken }));
        } else {
            return await dispatch(addLACreditCard({ address, cardholderName, cardToken, houseId, providerId }));
        }
    };

type AddStripeCreditCardParams = {
    cardToken: string;
    cardholderName: string;
};
const addStripeCreditCard =
    ({ cardToken }: AddStripeCreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            const isStripeCustomer = getIsStripeCustomer(state);

            dispatch({
                payload: bidderId,
                type: ADD_LA_PAYMENT_CARD_REQUEST,
            });

            const apiCall = isStripeCustomer ? putAddStripeCard : postAddStripeCard;
            const response = await apiCall({ authToken, bidderId, cardToken, deployment });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.data,
                type: ADD_LA_PAYMENT_CARD_SUCCESS,
            });

            // Update bidder profile
            await dispatch(fetchBidderDataIfNeeded(authToken, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: ADD_LA_PAYMENT_CARD_FAIL,
            });
            throw new Error(error);
        }
    };

type AddLACreditCardParams = {
    address: Address;
    cardToken: string;
    cardholderName: any;
    houseId: number;
    providerId: number;
};
//add is for creating new non-stripe cards. Currently includes payrix and paysafe.
const addLACreditCard =
    ({ address, cardholderName, cardToken, houseId, providerId }: AddLACreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: bidderId,
                type: ADD_LA_PAYMENT_CARD_REQUEST,
            });
            const response = await postAddLACard({
                authToken,
                bidderId,
                billingAddress: address,
                cardholderName: `${cardholderName.first} ${cardholderName.last}`,
                cardNonce: cardToken,
                deployment,
                houseId,
                providerId,
            });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: ADD_LA_PAYMENT_CARD_SUCCESS,
            });

            // Update user profile
            await dispatch(fetchBidderDataIfNeeded(authToken, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: ADD_LA_PAYMENT_CARD_FAIL,
            });
            throw new Error(error);
        }
    };

type UpdateLACreditCardParams = {
    billingAddress?: Address;
    cardId: number;
    defaultCard: boolean;
    enabled: boolean;
};
//Update is for updating the billing address of an existing card. The api can be used for disabling cards too, but the ui has no hookup for that.
export const updateLACreditCard =
    ({ billingAddress, cardId, defaultCard, enabled }: UpdateLACreditCardParams) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);
            dispatch({
                payload: bidderId,
                type: UPDATE_LA_PAYMENT_CARD_REQUEST,
            });

            // Send the request
            const response = await postUpdateLACard({
                authToken,
                bidderId,
                billingAddress,
                cardId,
                defaultCard,
                deployment,
                enabled,
            });
            dispatch({
                payload: response.payload,
                type: UPDATE_LA_PAYMENT_CARD_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_LA_PAYMENT_CARD_FAIL,
            });
        }
    };

const shouldFetchLAPaymentCards = createSelector(
    getLAPaymentCardsLoaded,
    getLAPaymentCardsLoading,
    (loaded: Date, loading: boolean) => {
        if (loading) {
            return false;
        }
        const time = Date.now();
        const diff = time - loaded?.getMilliseconds();
        return !Boolean(loaded) || diff > REDUX_STORE_TIME;
    }
);

export const fetchLAPaymentCardIfNeeded = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    if (shouldFetchLAPaymentCards(state)) {
        try {
            const authToken = getAuthToken(state);
            const bidderId = getBidderId(state);
            const deployment = getDeployment(state);

            // if bidderId is less than 1 the bidder data hasnt loaded yet and we can return.
            if (bidderId < 1) {
                return Promise.resolve();
            }

            dispatch({
                payload: authToken,
                type: LOAD_LA_PAYMENT_CARD_REQUEST,
            });
            const response = await getLAPaymentCards({ authToken, bidderId, deployment });
            return dispatch({
                meta: { actionTime: Date.now() },
                payload: response.payload,
                type: LOAD_LA_PAYMENT_CARD_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: LOAD_LA_PAYMENT_CARD_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
